
import React, { useState } from 'react';
import { toast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info } from 'lucide-react';

interface EmailFormProps {
  email: string;
  setEmail: (email: string) => void;
  onSkip: () => void;
  isSubmitting: boolean;
  setIsSubmitting: (isSubmitting: boolean) => void;
  onSuccess: () => void;
  onError?: (error: string) => void;
}

const EmailForm: React.FC<EmailFormProps> = ({ 
  email, 
  setEmail, 
  onSkip,
  isSubmitting,
  setIsSubmitting,
  onSuccess,
  onError
}) => {
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setErrorMsg(null);
    
    if (!email.trim()) {
      setErrorMsg("Please enter your email address.");
      toast({
        title: "Email required",
        description: "Please enter your email address.",
        variant: "destructive",
        className: "top-center"
      });
      return;
    }
    
    if (!validateEmail(email)) {
      setErrorMsg("Please enter a valid email address.");
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
        variant: "destructive",
        className: "top-center"
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Store email in localStorage for reference
      localStorage.setItem('userEmail', email);
      
      // Show success message
      toast({
        title: "Email saved!",
        description: "Taking you to your results.",
        className: "top-center"
      });
      
      // Call onSuccess to navigate to results
      onSuccess();
    } catch (error: any) {
      console.error("Error:", error);
      
      const errorMessage = error.message || "Something went wrong. Please try again.";
      setErrorMsg(errorMessage);
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
        className: "top-center"
      });
      
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleEmailSubmit} className="space-y-4">
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          Email Address
        </label>
        <Input 
          id="email"
          type="email" 
          placeholder="Your email address" 
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full"
          disabled={isSubmitting}
          aria-invalid={errorMsg ? "true" : "false"}
        />
      </div>
      
      {errorMsg && (
        <Alert variant="destructive" className="text-sm">
          <AlertDescription>{errorMsg}</AlertDescription>
        </Alert>
      )}
      
      <Button 
        type="submit" 
        className="w-full bg-morgen-gradient hover:opacity-90 transition-opacity"
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <span className="flex items-center">
            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
            Processing...
          </span>
        ) : (
          "Continue to Results"
        )}
      </Button>
      
      <div className="flex justify-center mt-2">
        <Button 
          type="button" 
          variant="ghost" 
          onClick={onSkip} 
          className="text-sm text-gray-500 hover:text-gray-700"
          disabled={isSubmitting}
        >
          Skip (continue without saving)
        </Button>
      </div>
      
      <div className="text-center text-sm text-gray-500 mt-4">
        <div className="flex items-center justify-center">
          <Info className="h-4 w-4 mr-1" />
          <p>Your email is only used to save your results</p>
        </div>
      </div>
    </form>
  );
};

export default EmailForm;
